import { Component, type ErrorInfo, type ReactNode } from 'react';
import { House, RefreshCw, TriangleAlert } from 'lucide-react';
import { Button } from '../ui/button';
import { Icon } from '../ui/icon';

export interface FehlergrenzeProps {
  children: ReactNode;
  /**
   * Meldet den Fehler weiter, etwa an Sentry. Auf der Konsole steht er
   * immer, auch ohne diese Prop.
   */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface FehlergrenzeState {
  error: Error | null;
  componentStack: string | null;
}

/**
 * Fängt einen Fehler beim Zeichnen ab und zeigt statt einer weißen Seite eine
 * ruhige Meldung mit „Seite neu laden“. Steht in jeder App einmal ganz außen
 * (`main.tsx`), braucht darum weder Router noch AppShell. Den Fehlertext
 * zeigt sie nur in der Entwicklung; Nutzer sehen keine Stack-Traces.
 * „Zur Startseite“ ist der Ausweg, wenn eine Seite bei jedem Laden abstürzt:
 * Die installierte App hat weder Zurück-Knopf noch Adresszeile.
 */
export class Fehlergrenze extends Component<FehlergrenzeProps, FehlergrenzeState> {
  state: FehlergrenzeState = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<FehlergrenzeState> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ componentStack: info.componentStack ?? null });
    console.error('Fehlergrenze:', error, info.componentStack);
    this.props.onError?.(error, info);
  }

  render() {
    const { error, componentStack } = this.state;
    if (!error) return this.props.children;

    return (
      <main className="flex min-h-dvh items-center justify-center bg-background px-4 py-10 text-foreground">
        <div role="alert" className="flex w-full max-w-md flex-col items-center gap-2 rounded-xl border border-border bg-card p-8 text-center">
          <span className="mb-3 flex size-14 items-center justify-center rounded-full bg-danger-soft text-danger">
            <Icon icon={TriangleAlert} size={28} />
          </span>
          <h1 className="t-page-title">Etwas ist schiefgelaufen</h1>
          <p className="text-muted-foreground">Die Seite ist auf einen unerwarteten Fehler gestoßen. Neu laden behebt das meist.</p>
          {import.meta.env.DEV && (
            <div className="mt-3 max-h-48 w-full overflow-auto rounded-md bg-muted p-3 text-left">
              <p className="break-all t-mono text-danger">{error.toString()}</p>
              {componentStack && <pre className="mt-2 whitespace-pre-wrap t-small text-muted-foreground">{componentStack}</pre>}
            </div>
          )}
          <div className="mt-4 flex w-full flex-col-reverse gap-2 sm:w-auto sm:flex-row">
            <Button size="lg" icon={House} onClick={() => window.location.assign('/')}>
              Zur Startseite
            </Button>
            <Button variant="primary" size="lg" icon={RefreshCw} onClick={() => window.location.reload()}>
              Seite neu laden
            </Button>
          </div>
        </div>
      </main>
    );
  }
}
